import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ChevronLeft,
  Package,
  Shield,
  Truck,
  Clock,
  Wrench
} from 'lucide-react';

const SuppliesStore = () => {
  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-40 glass-card border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
              <ChevronLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Back to OverWatch</span>
            </Link>
            <div className="h-6 w-px bg-slate-700" />
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-teal-500" />
              <h1 className="text-xl font-bold text-white">Emergency Supplies</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Coming Soon */}
      <main className="flex flex-col items-center justify-center min-h-[calc(100vh-80px)] px-4 py-16">
        <div className="max-w-3xl mx-auto text-center">
          {/* Tank and Daisy Avatars */}
          <div className="flex justify-center items-end gap-8 mb-8">
            {/* Tank's Avatar */}
            <div className="flex flex-col items-center">
              <div className="relative">
                <img 
                  src="/images/tank.png"
                  alt="Tank"
                  className="w-32 h-32 rounded-full border-4 border-amber-500/50 shadow-2xl shadow-amber-500/20 object-cover"
                />
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-amber-500 rounded-full border-4 border-slate-950 flex items-center justify-center">
                  <Wrench className="w-3 h-3 text-white" />
                </div>
              </div>
              <span className="mt-3 text-amber-400 font-bold text-lg">Tank</span>
              <span className="text-xs text-slate-500">Chief Logistics</span>
            </div>
            
            {/* Daisy's Avatar */}
            <div className="flex flex-col items-center">
              <div className="relative">
                <img 
                  src="/images/daisy.png"
                  alt="Daisy"
                  className="w-32 h-32 rounded-full border-4 border-purple-500/50 shadow-2xl shadow-purple-500/20 object-cover"
                />
                <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-purple-500 rounded-full border-4 border-slate-950 flex items-center justify-center">
                  <Package className="w-3 h-3 text-white" />
                </div>
              </div>
              <span className="mt-3 text-purple-400 font-bold text-lg">Daisy</span>
              <span className="text-xs text-slate-500">Inventory Specialist</span>
            </div>
          </div>
          
          {/* Badge */}
          <Badge className="mb-6 bg-amber-500/20 text-amber-400 border border-amber-500/30 px-4 py-1.5 text-sm">
            <Clock className="w-4 h-4 mr-2" />
            Coming Soon
          </Badge>
          
          {/* Title */}
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Bug-Out Bonanza
          </h2>
          
          {/* Subtitle */}
          <p className="text-xl text-slate-400 mb-8 max-w-xl mx-auto">
            We're building something special! Tank and Daisy are curating the best emergency supplies for the VeriWatch community.
          </p>
          
          {/* Quote from Tank */}
          <div className="bg-gradient-to-r from-amber-950/30 via-slate-900/50 to-amber-950/30 rounded-2xl p-6 mb-8 border border-amber-800/30 max-w-2xl mx-auto">
            <p className="text-amber-200/90 italic text-lg">
              "Hold your horses, partner! We're hand-pickin' only the finest survival gear. When this store opens, you'll have everything you need to weather any storm."
            </p>
            <p className="text-amber-500 font-bold mt-3 text-sm">— Tank</p>
          </div>
          
          {/* Features Preview */}
          <div className="grid md:grid-cols-3 gap-6 mb-10 max-w-2xl mx-auto">
            <div className="flex flex-col items-center p-4 rounded-xl bg-slate-900/50 border border-slate-800">
              <Package className="w-8 h-8 text-teal-400 mb-3" />
              <h3 className="font-semibold text-white mb-1">Curated Kits</h3>
              <p className="text-xs text-slate-500">Ready-to-go emergency bundles</p>
            </div>
            <div className="flex flex-col items-center p-4 rounded-xl bg-slate-900/50 border border-slate-800">
              <Truck className="w-8 h-8 text-teal-400 mb-3" />
              <h3 className="font-semibold text-white mb-1">Fast Shipping</h3>
              <p className="text-xs text-slate-500">Direct to your door</p>
            </div>
            <div className="flex flex-col items-center p-4 rounded-xl bg-slate-900/50 border border-slate-800">
              <Shield className="w-8 h-8 text-teal-400 mb-3" />
              <h3 className="font-semibold text-white mb-1">Quality Gear</h3>
              <p className="text-xs text-slate-500">Vetted by Tank himself</p>
            </div>
          </div>
          
          {/* Back Button */}
          <Link to="/dashboard">
            <Button className="bg-teal-600 hover:bg-teal-500 text-white px-8 py-3 text-lg">
              Return to OverWatch
            </Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-4 py-6 border-t border-slate-800">
        <div className="max-w-7xl mx-auto text-center text-sm text-slate-500">
          <p>VeriWatch Emergency Supplies • Coming Soon</p>
        </div>
      </footer>
    </div>
  );
};

export default SuppliesStore;
